import "./PriceLookup.css";
import GeneralHeader from "../../common/ui/GeneralHeader";
import MCLoadingBar from "../../common/ui/misc/MCLoadingBar";
import React from "react";
import {MC_Device} from "../../common/iot/MC_Device";
import {SimplePrice} from "../../common/iot/simple_stripe_types";
import {
    Button, Divider,
    InputBase,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import {alpha, styled} from "@mui/material/styles";
import {AttachMoney, LocalOffer} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import DeviceItemList from "../../common/ui/item-list/list/DeviceItemList";
import {MC_Backend} from "../../common/MC_Backend";
import {DevicesContext} from "../../App";
import {MC_Constants} from "../../common/MC_Constants";
import {useSearchParams} from "react-router-dom";

// Styled "Search" div
const searchColor: string = "#9b9b9b";
const SearchDiv = styled("div")(({theme}) => ({
    position: "relative",
    display: "flex",
    maxWidth: "350px",
    flexDirection: "row",
    justifyContent: "flex-start",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(searchColor, 0.15),
    '&:hover': {
        backgroundColor: alpha(searchColor, 0.25),
    },
    marginLeft: 0,
    width: "100%"
}));
const IconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));
const StyledInputBase = styled(InputBase)(({ theme }) => ({
    flexGrow: 1,
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%'
    },
}));

// Main react function
function PriceLookup(props: any) {
    // Simple state
    const [queryParams] = useSearchParams();
    const qp_priceID: string = "price_id";
    const qp_kitID: string = "kit_id";
    const queryPriceID: string | null = (queryParams.has(qp_priceID)) ? queryParams.get(qp_priceID) : null;
    const queryKitID: string | null = (queryParams.has(qp_kitID)) ? queryParams.get(qp_kitID) : null;
    const [priceID, setPriceID] = React.useState<string>((queryPriceID != null) ? queryPriceID : "");
    const [kitID, setKitID] = React.useState<string>((queryKitID != null) ? queryKitID : "");
    const [loading, setLoading] = React.useState<boolean>(false);
    const [err, setErr] = React.useState<string | null>(null);
    const [priceResult, setPriceResult] = React.useState<SimplePrice | null>(null);

    // Price tiers table rows
    let priceTierRows: JSX.Element[] = [];
    if (priceResult != null) {
        const tierLen: number = priceResult.tierPrices.length;
        let previousVolMax: number = 0;
        for (let i = 0; i < tierLen; i++) {
            const tierStartVolume = previousVolMax + 1;
            const tierMaxVolume = priceResult.tierVolumes[i];
            previousVolMax = tierMaxVolume;
            priceTierRows.push(
                <TableRow key={"" + i}>
                    <TableCell>{tierStartVolume} - {tierMaxVolume}</TableCell>
                    <TableCell align={"right"}>
                        <span className={"good"}>
                            <b>{MC_Constants.getUSDStr(priceResult.tierPrices[i], true)}</b>
                        </span>
                    </TableCell>
                </TableRow>
            );
        }
    }

    // [On initial load only] Search price ID from query param
    React.useEffect(() => {
        if (!loading && queryPriceID != null && priceID.length > 0) {
            searchFn().finally();
        }
    }, []);

    // Validate priceID & kitID (Don't allow them to be all white space)
    React.useEffect(() => {
        if (priceID.length > 0 && priceID.replace(/\s/g, '').length === 0) {
            setPriceID(""); // Clear it
        }
        if (kitID.length > 0 && kitID.replace(/\s/g, '').length === 0) {
            setKitID(""); // Clear it
        }
    }, [priceID, setPriceID, kitID, setKitID]);

    // Helper functions
    const getBillingPeriod = (price: SimplePrice): string => {
        let term: string;
        switch (price.subscriptionPeriod) {
            case "day":
                term = "Day(s)";
                break;
            case "week":
                term = "Week(s)";
                break;
            case "month":
                term = "Month(s)";
                break;
            case "year":
                term = "Year(s)";
                break;
            default:
                term = "Unknown";
        }
        return "Every " + price.subscriptionPeriodCount + " " + term;
    };
    const searchFn = async () => {
        // Take no action if an op is already in progress
        if (loading) {
            return;
        }
        // Error on invalid input
        if (priceID.length === 0) {
            setErr("You must enter a Stripe Price ID.");
            return;
        }
        // Attempt to load the info
        setLoading(true);
        MC_Backend.getInstance().getPriceInfo(priceID)
            .then((foundPrice: SimplePrice) => {
                // Update the UI with this price
                setPriceResult(foundPrice);
                setErr(null);
                setLoading(false);
            }).catch((err) => {
                console.error("Load price err: " + err);
                setPriceResult(null);
                setErr("Price ID not found.");
                setLoading(false);
        });
    };
    // UI
    return (
        <div className={"pl-root"}>

            {/* Title */}
            <GeneralHeader title={"Price Lookup"}/>

            {/* Description */}
            <p className={"small-note"}>
                Here you can find subscription pricing info for a specific device or sensor kit.
            </p>

            {/* Main content */}
            <div className={"all-pl-content"}>

                {/* Search controls */}
                <Paper className={"pl-content"} elevation={6}>
                    <div className={"search-ctls"}>

                        {/* Search price ID and optional kit ID */}
                        <div className={"search-fields-div"}>
                            <SearchDiv>
                                <IconWrapper>
                                    <AttachMoney />
                                </IconWrapper>
                                <StyledInputBase
                                    value={priceID}
                                    onChange={(ev) => setPriceID(ev.target.value)}
                                    placeholder={"Enter Stripe Price ID..."}
                                />
                            </SearchDiv>
                            <SearchDiv>
                                <IconWrapper>
                                    <LocalOffer />
                                </IconWrapper>
                                <StyledInputBase
                                    value={kitID}
                                    onChange={(ev) => setKitID(ev.target.value)}
                                    placeholder={"[Optional] Kit ID..."}
                                />
                            </SearchDiv>
                        </div>

                        {/* Search button */}
                        <Button className={"search-btn"}
                                variant="contained"
                                onClick={searchFn}>
                            <div className={"btn-content"}>
                                <SearchIcon/>
                                <span className={"btn-span"}>Search</span>
                            </div>
                        </Button>

                    </div>
                </Paper>

                {/* Loading div */}
                <div className={"full-width-div"}>
                    <MCLoadingBar loadingMessage={""} loading={loading} errorMessage={!loading ? err : null} />
                </div>

                {/* Price result */}
                {(!loading && priceResult != null) &&
                    <Paper className={"pl-content"} elevation={6}>
                        <span className={"pl-subtitle"}>Pricing Info</span>
                        <Divider className={"divider"} sx={{my: 1}} />
                        <span>Price ID: <b>{priceResult.id}</b></span>
                        <span>Product Name: <b>{priceResult.productName}</b></span>
                        <span>Billing Period: <b>{getBillingPeriod(priceResult)}</b></span>
                        {(priceResult.tierMode != null) &&
                            <span>Pricing Tier Mode: <b>{priceResult.tierMode}</b></span>
                        }
                        <span>
                            JAS Platform Fee: <b className={"orange"}>
                                {MC_Constants.getUSDStr(priceResult.jasFee, true)}
                            </b>
                        </span>
                        <span className={"jas-fee-note"}>JAS Fees are deducted automatically upon customer payment.</span>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Quantity</b></TableCell>
                                        <TableCell align={"right"}><b>Price Per Unit</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {priceTierRows}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                }

                {/* Devices results */}
                {(!loading && priceResult != null && kitID.length > 0) &&
                    <Paper className={"pl-content"} elevation={6}>
                        <span className={"pl-subtitle"}>Kit Devices</span>
                        <Divider className={"divider"} sx={{my: 1}} />
                        <DevicesContext.Consumer>
                            {(devicesCtx) => {
                                // React to status of devices list
                                const devicesLoaded: boolean = (devicesCtx.devices != null);
                                let kitDeviceIDs: string[] = [];
                                if (devicesLoaded) {
                                    //const filterByKitIDFn = (d: MC_Device) => );
                                    kitDeviceIDs = devicesCtx.devices!
                                        .filter((x: MC_Device) => {
                                            // The price ID & Kit ID must match
                                            const devicePriceIDs: string[] = [
                                                x.billingConfig.month_price_id || "",
                                                x.billingConfig.year_price_id || ""
                                            ];
                                            if (devicePriceIDs.includes(priceID)) {
                                                if (kitID.length > 0 && kitID === x.billingConfig.kit_id) {
                                                    return true; // Match found
                                                }
                                            }
                                            // Not a match
                                            return false;
                                        }).map((x) => x.id)
                                    ;
                                }
                                // UI (loading bar or filtered devices)
                                return <div className={"full-width-div"}>
                                    {/* Devices available to filter */}
                                    <DeviceItemList deviceIDs={kitDeviceIDs} />
                                </div>;
                            }}
                        </DevicesContext.Consumer>
                    </Paper>
                }

            </div>

            <br/><br/>

        </div>
    );
}

export default PriceLookup;